import { ChangeEvent, FocusEvent, useState } from 'react';
import currency from 'currency.js';
import { formatPhoneNumber } from '@trukt/portal-utils/formatPhoneNumber';
import { removeAllCharactersExceptNumbersAndDecimals } from '@trukt/portal-utils/removeAllCharactersExceptNumbersAndDecimals';
import { ArrowDown, ArrowUp, DollarSign } from '../../icons';
import { removeNumbers } from '@trukt/portal-utils/removeNumbers';
import { Typography } from '../../Typography';
import { TextFieldProps } from './TextField.types';
import useStyles from './TextField.styles';
import { cleanInputOnBlur, getIsInputDisabled } from './helpers';
import { useTheme } from '../../../hooks/useTheme';

export const TextField = ({
  value,
  variant = 'text',
  maxLength,
  label,
  required = false,
  isCharacterCounterEnabled = false,
  errorMessage,
  caption,
  styles: customStyles,
  onArrowUp,
  onArrowDown,
  icon,
  iconPosition = 'left',
  onIconClick,
  testId,
  isLabelOnLeft = false,
  typographyClassName,
  prohibitNumbers = false,
  onChange = () => {},
  onBlur = () => {},
  colorText = 'primary',
  error,
  noLabel = false,
  onFocus = () => {},
  startContent = null,
  endContent = null,
  showErrorWhenDisabled = false,
  isDisabled,
  isFakeDisabled,
  disabled,
  classNames,
  ...rest
}: TextFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const isCurrency = variant === 'currency';

  const isInputDisabled = getIsInputDisabled({ disabled, isDisabled, isFakeDisabled });
  const styles = useStyles(
    {
      colorText,
      label,
      error,
      isLabelOnLeft,
      onArrowUp,
      onArrowDown,
      noLabel,
      showErrorWhenDisabled,
      ...rest,
    },
    isInputDisabled,
  );

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    let { value } = e.target;
    value = value.trim();

    if (variant === 'text') {
      value = cleanInputOnBlur(value);
    } else if (variant === 'phone') {
      value = formatPhoneNumber(value);
    } else if (variant === 'number') {
      value = removeAllCharactersExceptNumbersAndDecimals(value);
    } else if (isCurrency) {
      setIsFocused(false);
    }

    e.target.value = value;

    onChange(e);
    onBlur(e);
  };

  const isActiveIcon = isInputDisabled || !onIconClick;
  const handleOnFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (isCurrency) {
      setIsFocused(true);
    }
    onFocus(e);
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (prohibitNumbers) {
      event.target.value = removeNumbers(event.target.value);
    } else if (isCurrency) {
      event.target.value = removeAllCharactersExceptNumbersAndDecimals(event.target.value);
    }

    onChange(event);
  };

  const getValue = () => {
    if (isCurrency && !isFocused) {
      return value?.toString()?.trim().length && typeof value === 'string'
        ? currency(value).toString()
        : '';
    }

    return value;
  };

  return (
    <div
      data-testid={testId}
      className={[classNames?.mainContainer, isLabelOnLeft ? 'w-full' : ''].join(' ')}
      css={{ ...customStyles?.mainContainer }}
    >
      <Typography className={typographyClassName}>
        <div
          data-testid="TextField-labelInputContainer"
          className={classNames?.labelInputContainer}
          css={isLabelOnLeft ? styles.labelInputContainer : undefined}
        >
          <div
            className={classNames?.labelContainer}
            css={[styles.labelContainer, customStyles?.labelContainer]}
          >
            {label}
            {!isInputDisabled && label && required && (
              <span
                className={classNames?.requiredAsterisk}
                css={[styles.requiredAsterisk, customStyles?.requiredAsterisk]}
              >
                {String.fromCharCode(42)}
              </span>
            )}

            {label && isCharacterCounterEnabled && (
              <span
                className={classNames?.characterCounter}
                css={[styles.characterCounter, customStyles?.characterCounter]}
              >{`${value?.toString?.().length ?? 0}/${maxLength ?? 0}`}</span>
            )}
          </div>

          <div
            data-testid="input__container"
            className={classNames?.inputContainer}
            css={[styles.inputContainer, customStyles?.inputContainer]}
          >
            {(icon || isCurrency) && (
              <div
                tabIndex={isActiveIcon ? -1 : 0}
                role="button"
                onKeyDown={() => {}}
                data-testid="input__icon"
                onClick={onIconClick ?? (() => {})}
                className={[
                  isActiveIcon ? 'cursor-default' : 'cursor-pointer',
                  iconPosition === 'right' ? 'order-1' : 'order-[0]',
                  disabled ? 'pointer-events-none' : 'pointer-events-auto',
                  'flex [&>svg]:size-[15px]',
                ].join(' ')}
              >
                {/* @ts-expect-error */}
                {icon && !isCurrency && (typeof icon === 'function' ? icon() : icon)}
                {isCurrency && <DollarSign />}
              </div>
            )}
            {startContent}
            <input
              value={getValue()}
              required={required}
              disabled={isInputDisabled}
              className={classNames?.input}
              css={[styles.input, customStyles?.input]}
              maxLength={maxLength}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              {...rest}
            />
            {endContent}
            <div
              className={classNames?.arrowContainer}
              css={[styles.arrowContainer, customStyles?.arrowContainer]}
            >
              {onArrowUp && (
                <ArrowUp
                  aria-hidden="true"
                  width={8}
                  height={8}
                  color={theme.palette.action.primary}
                  strokeWidth={3}
                  className="cursor-pointer"
                  data-testid="input__arrow-up"
                  onClick={onArrowUp}
                />
              )}
              {onArrowDown && (
                <ArrowDown
                  aria-hidden="true"
                  width={8}
                  height={8}
                  color={theme.palette.action.primary}
                  strokeWidth={3}
                  className="cursor-pointer"
                  data-testid="input__arrow-down"
                  onClick={onArrowDown}
                />
              )}
            </div>
          </div>
        </div>
      </Typography>
      {(!isInputDisabled || showErrorWhenDisabled) && errorMessage && (
        <Typography className="!text-error-400">{errorMessage}</Typography>
      )}
      {caption && <Typography>{caption}</Typography>}
    </div>
  );
};
